import React, { useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import DraftsOutlinedIcon from "@mui/icons-material/DraftsOutlined";
import RequestPageOutlinedIcon from "@mui/icons-material/RequestPageOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import UnfoldMoreOutlinedIcon from "@mui/icons-material/UnfoldMoreOutlined";
import UnfoldLessOutlinedIcon from "@mui/icons-material/UnfoldLessOutlined";
import { permissionCategories } from "../../utils/permissionConfig";

// Presentation only, shared by Add Role and Edit Role. Categories, permission
// values and the checked state all come from the existing permissionConfig /
// form state; the parent's own onCategoryChange (Select All) and
// onPermissionChange handlers are called exactly as before. Search and
// expand/collapse only change what is displayed, never which permissions
// are selected.
const CATEGORY_META = {
  "Mail Room": { icon: MailOutlineIcon, tone: "bg-[#e6f0ff] text-[#2563eb]", description: "Manage mail room related documents and communications" },
  "All Documents": { icon: ArticleOutlinedIcon, tone: "bg-[#e6f7ef] text-[#16a37a]", description: "Manage document access and document requests" },
  "Unprocessed Documents": { icon: PendingActionsOutlinedIcon, tone: "bg-[#f1e9ff] text-[#7c4dce]", description: "Access and manage unprocessed documents" },
  Documents: { icon: DescriptionOutlinedIcon, tone: "bg-[#fde8f1] text-[#d0387a]", description: "Manage document operations and actions" },
  Clients: { icon: PeopleOutlineIcon, tone: "bg-[#fff1e3] text-[#e07a1b]", description: "Manage client related actions and requests" },
  Settings: { icon: SettingsOutlinedIcon, tone: "bg-[#e6f2ff] text-[#2b6fa8]", description: "Access system settings" },
  Users: { icon: GroupOutlinedIcon, tone: "bg-[#e3f5f8] text-[#1a6b7c]", description: "Manage user accounts" },
  "Field Settings": { icon: TuneOutlinedIcon, tone: "bg-[#e6f7ef] text-[#16a37a]", description: "Configure document and client fields" },
  "Email Templates": { icon: DraftsOutlinedIcon, tone: "bg-[#e6f0ff] text-[#2563eb]", description: "Manage reusable email templates" },
  "Document Requests": { icon: RequestPageOutlinedIcon, tone: "bg-[#fff6dc] text-[#c98a00]", description: "Manage document request templates" },
  Delete: { icon: DeleteOutlineIcon, tone: "bg-[#fdeaea] text-[#d14343]", description: "Access the Recycle Bin" },
  "Delete Users": { icon: PersonRemoveOutlinedIcon, tone: "bg-[#f1e9ff] text-[#7c4dce]", description: "View, restore or permanently delete users" },
  "Delete Documents": { icon: DeleteSweepOutlinedIcon, tone: "bg-[#fde8f1] text-[#d0387a]", description: "View, restore or permanently delete documents" },
};
const DEFAULT_META = { icon: ShieldOutlinedIcon, tone: "bg-[#e3f5f8] text-[#1a6b7c]", description: "" };

const CHECKBOX_CLASS = "h-4 w-4 shrink-0 cursor-pointer rounded border-gray-300 accent-[#1a6b7c]";

const PermissionSections = ({ permissions = [], onCategoryChange, onPermissionChange, error }) => {
  const [search, setSearch] = useState("");
  // All sections open by default so every permission is visible on load.
  const [collapsed, setCollapsed] = useState({});

  const query = search.trim().toLowerCase();
  const sections = permissionCategories
    .map((category) => {
      if (!query || category.name.toLowerCase().includes(query)) {
        return { category, visibleChildren: category.children };
      }
      const visibleChildren = category.children.filter((p) => p.name.toLowerCase().includes(query));
      return visibleChildren.length ? { category, visibleChildren } : null;
    })
    .filter(Boolean);

  const toggle = (name) => setCollapsed((prev) => ({ ...prev, [name]: !prev[name] }));
  const expandAll = () => setCollapsed({});
  const collapseAll = () =>
    setCollapsed(Object.fromEntries(permissionCategories.map((c) => [c.name, true])));

  // While searching, every matching section is shown open.
  const isSectionOpen = (name) => !!query || !collapsed[name];

  return (
    <div className="role-permissions rounded-xl border border-[#d7ecef] bg-[#f6fbfc] p-3">
      {/* Section header */}
      <div className="mb-2.5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#e3f5f8] text-[#1a6b7c]">
            <LockOutlinedIcon sx={{ fontSize: 20 }} />
          </span>
          <div className="min-w-0">
            <div className="text-[15px] font-semibold leading-tight text-gray-900">Assign Permissions</div>
            <div className="text-xs text-gray-500">Select the modules and permissions to include in this role</div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex h-9 min-w-[200px] flex-1 items-center gap-2 rounded-lg border border-gray-200 bg-white px-2.5 sm:w-72 sm:flex-none">
            <SearchIcon sx={{ fontSize: 18 }} className="text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search permissions..."
              className="min-w-0 flex-1 border-0 bg-transparent text-[13px] outline-none placeholder:text-gray-400"
            />
          </label>
          <button
            type="button"
            onClick={expandAll}
            className="flex h-9 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-[13px] font-semibold text-[#1a6b7c] hover:bg-gray-50"
          >
            <UnfoldMoreOutlinedIcon sx={{ fontSize: 18 }} />
            Expand All
          </button>
          <button
            type="button"
            onClick={collapseAll}
            className="flex h-9 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-[13px] font-semibold text-[#1a6b7c] hover:bg-gray-50"
          >
            <UnfoldLessOutlinedIcon sx={{ fontSize: 18 }} />
            Collapse All
          </button>
        </div>
      </div>

      {/* Compact, full-width expandable sections */}
      {sections.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-200 bg-white px-4 py-6 text-center text-sm text-gray-500">
          No permissions match "{search}".
        </div>
      ) : (
        <div className="permission-categories flex flex-col gap-1.5">
          {sections.map(({ category, visibleChildren }) => {
            const meta = CATEGORY_META[category.name] || DEFAULT_META;
            const Icon = meta.icon;
            const isOpen = isSectionOpen(category.name);
            const allChecked = category.children.every((child) => permissions.includes(child.value));
            const selectedCount = category.children.filter((child) => permissions.includes(child.value)).length;
            return (
              <div key={category.name} className="category overflow-hidden rounded-lg border border-gray-200 bg-white">
                <div
                  className={`category-title flex items-center gap-3 px-3 py-1.5 ${isOpen ? "bg-[#f8fbfc]" : ""}`}
                >
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${meta.tone}`}>
                    <Icon sx={{ fontSize: 18 }} />
                  </span>
                  <button
                    type="button"
                    onClick={() => toggle(category.name)}
                    className={`flex min-w-0 flex-1 text-left ${
                      isOpen ? "flex-col" : "flex-row items-baseline gap-2"
                    }`}
                  >
                    <span className="flex shrink-0 items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900">{category.name}</span>
                      {selectedCount > 0 && (
                        <span className="rounded-full bg-[#e3f5f8] px-1.5 text-[10px] font-semibold text-[#1a6b7c]">
                          {selectedCount}/{category.children.length}
                        </span>
                      )}
                    </span>
                    {meta.description && (
                      <span className="min-w-0 truncate text-xs text-gray-500">{meta.description}</span>
                    )}
                  </button>
                  <label
                    htmlFor={category.name}
                    className="flex shrink-0 cursor-pointer items-center gap-1.5 text-xs font-medium text-gray-700"
                  >
                    <input
                      type="checkbox"
                      id={category.name}
                      className={CHECKBOX_CLASS}
                      onChange={(e) => onCategoryChange(category, e.target.checked)}
                      checked={allChecked}
                    />
                    Select All
                  </label>
                  <button
                    type="button"
                    onClick={() => toggle(category.name)}
                    aria-label={isOpen ? `Collapse ${category.name}` : `Expand ${category.name}`}
                    aria-expanded={isOpen}
                    className="shrink-0 rounded-md p-0.5 text-gray-500 hover:bg-gray-100"
                  >
                    {isOpen ? (
                      <KeyboardArrowUpIcon sx={{ fontSize: 20 }} />
                    ) : (
                      <KeyboardArrowDownIcon sx={{ fontSize: 20 }} />
                    )}
                  </button>
                </div>
                {isOpen && (
                  <div className="category-permissions grid grid-cols-1 gap-x-4 gap-y-1 border-t border-gray-100 px-4 py-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5">
                    {visibleChildren.map((permission) => (
                      <label
                        key={permission.value}
                        htmlFor={permission.value}
                        className="permission flex min-w-0 cursor-pointer items-center gap-2 py-0.5 text-[13px] text-gray-700"
                      >
                        <input
                          type="checkbox"
                          id={permission.value}
                          className={CHECKBOX_CLASS}
                          onChange={() => onPermissionChange(permission)}
                          checked={permissions.includes(permission.value)}
                        />
                        <span className="min-w-0 truncate" title={permission.name}>
                          {permission.name}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {error && <span className="error-message mt-2 block">{error}</span>}
    </div>
  );
};

export default PermissionSections;
